import React from 'react';
import classNames from 'classnames';
import styles from './UserAttendance.module.scss';
import { Dropdown, filterBarSelectStyles } from '@/components/Dropdown';
import BlockUi from 'react-block-ui';
import { userLeaveBalanceConfig } from './UserAttendance.constants';
import { fontWeightEnum, Text, textVariantsEnum } from '@/components/Text';
import {
  FiCalendar,
  FiTrendingUp,
  FiChevronLeft,
  FiChevronRight,
} from 'react-icons/fi';
import { Button } from 'reactstrap';
import { Button as AppButton, buttonVariants } from '@/components/Button';
import { StatCard } from '@/components/StatsCards';
import { DonutBreakdown } from '@/components/DonutBreakdown';
import { MetricTile } from '@/components/MetricTile';
import { MonthCalendarGrid } from '@/components/MonthCalendarGrid';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import { CiExport } from 'react-icons/ci';

// Pure presentational — no Redux/react-query/service calls. Rendered by both
// UserAttendance.component.jsx (real) and UserAttendance.demo.component.jsx
// (static fixture-backed); a future redesign only ever needs to touch this
// file plus whichever container's props it changes.
export const UserAttendanceView = ({
  showInitialLoader,
  membersError,
  membersErrorObj,
  refetchMembers,
  activeTeamId,
  options,
  selectOption,
  onSelectChange,
  membersLoading,
  currentMonth,
  onMonthChange,
  onPrevMonth,
  onNextMonth,
  exportLoading,
  isExportingMember,
  isExportingTeam,
  onExportMember,
  trendByKey,
  prevMonthShortLabel,
  leaveBalanceConfig,
  isCalendarLoading,
  getDayContent,
  totalDaysTracked,
  donutSegments,
  workingDays,
  attendancePct,
}) => {
  if (showInitialLoader) {
    return (
      <div className="loader-container">
        <BlockUi tag="div" blocking message="Loading, please wait" />
      </div>
    );
  }

  return (
    <div className={styles.userAttendancePage}>
      {membersError && activeTeamId && (
        <div className="mb-1 text-danger small">
          Failed to load members. {membersErrorObj?.message || ''}{' '}
          <Button
            color="link"
            size="sm"
            className="p-0"
            onClick={() => refetchMembers()}
          >
            Retry
          </Button>
        </div>
      )}
      <div className={styles.filtersContainer}>
        <div className={styles.dropdown}>
          <Dropdown
            options={options}
            value={selectOption}
            placeholder="Select member..."
            onChange={onSelectChange}
            isLoading={membersLoading}
            isDisabled={!activeTeamId || membersError}
            selectStyles={filterBarSelectStyles}
          />
        </div>

        <div className={styles.monthField}>
          <DatePicker
            className={styles.monthYearPicker}
            popupClassName={styles.monthPopup}
            placement="bottomLeft"
            getPopupContainer={(triggerNode) => triggerNode.parentElement}
            picker="month"
            allowClear={false}
            suffixIcon={null}
            prefix={<FiCalendar size={16} className={styles.monthFieldIcon} />}
            format="MMMM YYYY"
            value={dayjs(currentMonth)}
            onChange={(d) => {
              if (!d) return;
              onMonthChange(d.startOf('month').toDate());
            }}
          />
          <div className={styles.monthArrows}>
            <button
              type="button"
              className={styles.monthArrowButton}
              aria-label="Previous month"
              onClick={onPrevMonth}
            >
              <FiChevronLeft size={18} />
            </button>
            <button
              type="button"
              className={styles.monthArrowButton}
              aria-label="Next month"
              onClick={onNextMonth}
            >
              <FiChevronRight size={18} />
            </button>
          </div>
        </div>

        <div className={styles.exportButtonContainer}>
          <AppButton
            variant={buttonVariants.outline}
            text="Export"
            icon={<CiExport size={16} />}
            loading={exportLoading && isExportingMember}
            disabled={exportLoading && isExportingTeam}
            onClick={onExportMember}
          />
          {/* <AppButton
            variant={buttonVariants.primary}
            text="Export Department Attendance"
            icon={<CiExport size={16} />}
            loading={exportLoading && isExportingTeam}
            disabled={exportLoading && isExportingMember}
            onClick={onExportTeam}
          /> */}
        </div>
      </div>
      {membersError && (
        <div className={styles.memberError}>
          <Text
            variant={textVariantsEnum.Text_sm}
            fontWeight={fontWeightEnum.Medium}
          >
            Failed to load members. {membersErrorObj?.message || ''}
          </Text>
          <Button color="link" size="sm" onClick={() => refetchMembers()}>
            Retry
          </Button>
        </div>
      )}

      <div className={styles.statsRow}>
        {userLeaveBalanceConfig.map((item) => {
          const Icon = item.icon;
          const trend = trendByKey[item.key];
          return (
            <StatCard
              key={item.key}
              title={item.label.toUpperCase()}
              value={leaveBalanceConfig[item.key]}
              icon={<Icon size={14} color={`var(--${item.iconColor})`} />}
              iconBg={item.iconBg}
              contaninerClassName={styles.statCardTile}
              trend={
                trend
                  ? {
                      text: `${trend.direction === 'up' ? '↑' : '↓'} ${
                        trend.pct
                      }% vs ${prevMonthShortLabel}`,
                    }
                  : undefined
              }
              compact
            />
          );
        })}
      </div>

      <div className={styles.pageBody}>
        <div className={styles.calendarContainer}>
          {isCalendarLoading ? (
            <div className="loader-container">
              <BlockUi tag="div" blocking message="Loading, please wait" />
            </div>
          ) : (
            <MonthCalendarGrid
              monthDate={currentMonth}
              getDayContent={getDayContent}
            />
          )}
          <div className={styles.calendarLegend}>
            {userLeaveBalanceConfig.map((item) => (
              <div key={item.key} className={styles.legendChip}>
                <span
                  className={styles.legendDot}
                  style={{ background: item.color }}
                />
                <Text variant={textVariantsEnum.Text_xs} color="gray-600">
                  {item.label}
                </Text>
              </div>
            ))}
          </div>
        </div>

        <div className={styles.sidebarColumn}>
          <div className={styles.sidebarCard}>
            <DonutBreakdown
              title="Attendance Overview"
              centerValue={totalDaysTracked}
              centerLabel="Total Days"
              segments={donutSegments}
            />
          </div>

          <div className={styles.sidebarCard}>
            <Text
              variant={textVariantsEnum.Text_md}
              fontWeight={fontWeightEnum.Semibold}
            >
              Working Summary
            </Text>
            <div className={styles.workingSummaryRow}>
              <MetricTile
                value={workingDays}
                label="Working Days"
                icon={<FiCalendar size={16} color="var(--purple-600)" />}
              />
              <MetricTile
                value={`${attendancePct}%`}
                label="Attendance %"
                icon={<FiTrendingUp size={16} color="var(--purple-600)" />}
              />
            </div>
          </div>

          {/* <div
            className={classNames(styles.sidebarCard, styles.sidebarCard_grow)}
          >
            <RecentActivityList
              title="Recent Activity"
              items={recentActivityItems}
            />
          </div> */}
        </div>
      </div>
    </div>
  );
};
